import { Button, CircularProgress, Tooltip } from '@mui/material';
import * as React from 'react';

import { ErrorAlert } from '../components';
import type { Integration, IntegrationAccount, IntegrationProbe } from '../integrationTypes';
import { ProbeResult } from './CredentialFields';
import { credentialsReady } from './integrationLabels';
import { useAction, useIntegrations } from './IntegrationsContext';

/** [Check now]: the read-only vendor probe of the stored credentials, where the system has one (plan §1.7). The
 *  card header checks a single-account connection; with several accounts each row checks its own (`account`). */
const useCheckNow = (connection: Integration, account?: IntegrationAccount) => {
    const { api } = useIntegrations();
    const action = useAction();
    const [probe, setProbe] = React.useState<IntegrationProbe | null>(null);
    const credentials = (account || connection.accounts[0])?.credentials;
    const ready = credentialsReady(credentials);
    const hint = credentials && credentials.keys.length === 0 ? 'Nothing to check: no credentials of its own' : 'Set the credentials first';
    const label = account ? 'Check' : 'Check now';
    const button = (
        <Tooltip title={ready ? `Ask ${connection.name} whether it accepts the stored credentials (read-only)` : hint}>
            <span>
                <Button
                    size="small"
                    variant={account ? 'text' : 'outlined'}
                    aria-label={account ? `Check ${account.display_name}` : undefined}
                    disabled={!ready || action.busy}
                    onClick={async () => {
                        setProbe(null);
                        const outcome = await action.run(() => api.check(connection.id, account ? { account: account.slug } : {}));
                        if (outcome.ok) {
                            setProbe(outcome.value);
                        }
                    }}
                    startIcon={action.busy ? <CircularProgress size={14} /> : undefined}
                >
                    {label}
                </Button>
            </span>
        </Tooltip>
    );
    const result = (
        <>
            {probe && <ProbeResult probe={probe} onClose={() => setProbe(null)} />}
            {action.error ? <ErrorAlert error={action.error} /> : null}
        </>
    );
    return { button, result };
};

export default useCheckNow;
